import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Loader2, Trash2, XCircle } from "lucide-react";
import { api, errorMessage, fileUrl, uploadWithProgress } from "../api/client";
import type { AiModel, Paged, Part, TrainingImage, TrainResponse, UploadResult } from "../api/types";
import { Button, Card, cx, Input, Select } from "../components/Controls";
import { Badge } from "../components/Badge";
import { Dropzone } from "../components/Dropzone";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { ImageLightbox } from "../components/ImageLightbox";
import { EmptyState } from "../components/EmptyState";
import { toast } from "../components/Toast";
import { fmtDateTime, fmtNum, fmtPct } from "../lib/format";
import { ModelEvaluationCard } from "./ModelEvaluationCard";
import { TryPhotoCard } from "./TryPhotoCard";

const MIN_OK = 50;
type UploadRow = { name: string; pct: number; status: "pending" | "done" | "error"; reason?: string };

function UploadBlock({ part, onDone }: { part: Part; onDone: () => void }) {
  const [label, setLabel] = useState<"OK" | "NG">("OK");
  const [point, setPoint] = useState("");
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [rejected, setRejected] = useState<{ filename: string; reason: string }[]>([]);
  const [busy, setBusy] = useState(false);

  const upload = async (files: File[]) => {
    setBusy(true);
    setRejected([]);
    setRows(files.map((f) => ({ name: f.name, pct: 0, status: "pending" })));
    const update = (i: number, patch: Partial<UploadRow>) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
    let uploaded = 0;
    const rej: { filename: string; reason: string }[] = [];
    let next = 0;
    const worker = async () => {
      while (next < files.length) {
        const i = next++;
        const fd = new FormData();
        fd.append("files", files[i]);
        fd.append("label", label);
        if (point !== "") fd.append("point_index", point);
        try {
          const r = await uploadWithProgress<UploadResult>(`/parts/${part.id}/images`, fd, (pct) => update(i, { pct }));
          uploaded += r.uploaded;
          r.rejected.forEach((x) => rej.push(x));
          update(i, r.uploaded ? { pct: 100, status: "done" } : { status: "error", reason: r.rejected[0]?.reason });
        } catch (e) {
          rej.push({ filename: files[i].name, reason: errorMessage(e) });
          update(i, { status: "error", reason: errorMessage(e) });
        }
      }
    };
    await Promise.all([worker(), worker(), worker(), worker()]);
    setRejected(rej);
    setBusy(false);
    if (uploaded) toast.success(`${uploaded} fotos subidas`);
    onDone();
  };

  const doneCount = rows.filter((r) => r.status !== "pending").length;
  return (
    <Card className="p-5" data-testid="training-upload-block">
      <h3 className="mb-3 font-display text-base font-semibold">Fotos de entrenamiento</h3>
      <div className="mb-3 grid grid-cols-2 gap-3 sm:max-w-md">
        <label className="text-sm font-medium text-slate-700">Etiqueta
          <Select value={label} onChange={(e) => setLabel(e.target.value as "OK" | "NG")} className="mt-1 block w-full" data-testid="upload-label-select">
            <option value="OK">OK</option>
            <option value="NG">NG</option>
          </Select>
        </label>
        <label className="text-sm font-medium text-slate-700">Punto
          <Select value={point} onChange={(e) => setPoint(e.target.value)} className="mt-1 block w-full" data-testid="upload-point-select">
            <option value="">Cualquiera</option>
            {part.points.map((p, i) => <option key={i} value={i}>{`Punto ${i + 1} · ${p}`}</option>)}
          </Select>
        </label>
      </div>
      <Dropzone multiple allowFolder onFiles={upload} label="Arrastrar fotos o una carpeta completa" testId="training-dropzone" />
      {rows.length > 0 && (
        <div className="mt-3 space-y-1" data-testid="upload-progress-list">
          <div className="text-xs text-slate-500">{busy ? `Subiendo ${doneCount} de ${rows.length}…` : `Terminado: ${doneCount} de ${rows.length}`}</div>
          <div className="max-h-40 space-y-1 overflow-y-auto pr-1">
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="w-40 truncate font-mono text-slate-600">{r.name}</span>
                <div className="h-1.5 flex-1 overflow-hidden rounded bg-slate-200">
                  <div className={cx("h-full transition-all", r.status === "error" ? "bg-ng" : "bg-signal")} style={{ width: `${r.status === "error" ? 100 : r.pct}%` }} />
                </div>
                {r.status === "done" && <CheckCircle2 className="h-3.5 w-3.5 text-ok" />}
                {r.status === "error" && <XCircle className="h-3.5 w-3.5 text-ng" />}
              </div>
            ))}
          </div>
        </div>
      )}
      {rejected.length > 0 && (
        <div className="mt-3 rounded-lg border border-ng/30 bg-ng-soft p-3 text-xs text-ng" data-testid="upload-rejected-list">
          <div className="mb-1 font-semibold">Rechazadas ({rejected.length})</div>
          {rejected.map((r, i) => <div key={i}><span className="font-mono">{r.filename}</span>: {r.reason}</div>)}
        </div>
      )}
    </Card>
  );
}

function Counter({ part }: { part: Part }) {
  const ok = part.image_counts.ok;
  const reached = ok >= MIN_OK;
  return (
    <Card className="p-5" data-testid="training-counter">
      <div className="font-display text-3xl font-semibold tracking-tight" data-testid="training-ok-count">{ok} fotos OK <span className="text-base font-normal text-slate-400">· mínimo {MIN_OK}</span></div>
      <div className="mt-3 h-2 overflow-hidden rounded bg-slate-200">
        <div className={cx("h-full transition-all duration-500", reached ? "bg-ok" : "bg-warn")} style={{ width: `${Math.min(100, (ok / MIN_OK) * 100)}%` }} data-testid="training-progress-bar" data-reached={reached} />
      </div>
      <div className="mt-3 text-sm text-slate-600" data-testid="training-ng-count">{part.image_counts.ng} fotos NG (para validar)</div>
      {part.image_counts.feedback > 0 && <div className="text-sm text-slate-500">{part.image_counts.feedback} fotos de feedback</div>}
      <p className="mt-2 text-xs text-slate-400">Las fotos NG no se usan para entrenar, solo para medir el modelo. De las OK se aparta un 20 % (mínimo 5) que el modelo no ve, para calibrar el umbral.</p>
    </Card>
  );
}

function Gallery({ part, version, onChanged }: { part: Part; version: number; onChanged: () => void }) {
  const [tab, setTab] = useState<"OK" | "NG" | "feedback">("OK");
  const [point, setPoint] = useState("");
  const [items, setItems] = useState<TrainingImage[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const load = useCallback(async (p: number) => {
    const q = tab === "feedback" ? { source: "feedback" } : { label: tab, source: "upload" };
    const r = await api.get<Paged<TrainingImage>>(`/parts/${part.id}/images`, { ...q, point_index: point, page: p, limit: 60 });
    setItems((prev) => (p === 1 ? r.items : [...prev, ...r.items]));
    setTotal(r.total);
    setPage(p);
  }, [part.id, tab, point]);

  useEffect(() => {
    setSelected(new Set());
    load(1).catch(() => undefined);
  }, [load, version]);

  const toggle = (id: string) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });

  const removeSelected = async () => {
    const ids = Array.from(selected);
    for (let i = 0; i < ids.length; i += 8) await Promise.all(ids.slice(i, i + 8).map((id) => api.del(`/training_images/${id}`)));
    toast.success(`${ids.length} fotos borradas`);
    onChanged();
  };

  const tabs: [typeof tab, string][] = [["OK", "OK"], ["NG", "NG"], ["feedback", "Feedback"]];
  return (
    <Card className="p-5" data-testid="training-gallery">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={cx("rounded-md px-3 py-1.5 text-sm font-medium", tab === k ? "bg-ink text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200")} data-testid={`gallery-tab-${k.toLowerCase()}`}>{l}</button>
        ))}
        <Select value={point} onChange={(e) => setPoint(e.target.value)} className="w-40" data-testid="gallery-point-filter">
          <option value="">Todos los puntos</option>
          {part.points.map((p, i) => <option key={i} value={i}>{`Punto ${i + 1}`}</option>)}
        </Select>
        <span className="text-sm text-slate-500" data-testid="gallery-total">{total} fotos</span>
        {selected.size > 0 && (
          <Button variant="danger" size="sm" className="ml-auto" onClick={() => setConfirm(true)} data-testid="gallery-delete-selected-button"><Trash2 className="h-3.5 w-3.5" /> Borrar seleccionadas ({selected.size})</Button>
        )}
      </div>
      {items.length === 0 ? <EmptyState title="Sin fotos en esta pestaña" text="Subir fotos con el bloque de arriba." testId="gallery-empty" /> : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3 sm:grid-cols-[repeat(auto-fill,160px)]">
          {items.map((img) => (
            <div key={img.id} className={cx("group relative overflow-hidden rounded-md border-2", selected.has(img.id) ? "border-signal" : "border-transparent")} data-testid="gallery-item">
              <img src={fileUrl(img.thumb_url)} alt="" className="aspect-[4/3] w-full cursor-zoom-in object-cover" onClick={() => setLightbox(fileUrl(img.url))} loading="lazy" />
              <input type="checkbox" checked={selected.has(img.id)} onChange={() => toggle(img.id)} className="absolute left-1.5 top-1.5 h-4 w-4 accent-signal" data-testid="gallery-item-checkbox" />
              <span className="absolute bottom-1 left-1 rounded bg-ink/80 px-1.5 py-0.5 font-mono text-[10px] text-white">{img.point_index === null ? "Pieza" : `P${img.point_index + 1}`}</span>
            </div>
          ))}
        </div>
      )}
      {items.length < total && <div className="mt-4 text-center"><Button variant="secondary" size="sm" onClick={() => load(page + 1)} data-testid="gallery-load-more">Cargar más</Button></div>}
      <ConfirmDialog open={confirm} onClose={() => setConfirm(false)} danger title="Borrar fotos" message={`Se van a borrar ${selected.size} fotos de entrenamiento.`} confirmLabel="Borrar" onConfirm={removeSelected} />
      <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />
    </Card>
  );
}

function StatusCard({ model, onRetry }: { model: AiModel; onRetry: () => void }) {
  if (model.status === "training") {
    return (
      <Card className="flex items-center gap-3 border-signal/30 bg-signal-soft p-4" data-testid="training-status-card" data-status="training">
        <Loader2 className="h-5 w-5 animate-spin text-signal" />
        <div><div className="font-semibold">Entrenando…</div><div className="text-xs text-slate-500">Con {model.image_count + model.image_count_feedback} fotos OK. Se actualiza cada 5 s.</div></div>
      </Card>
    );
  }
  if (model.status === "failed") {
    return (
      <Card className="flex flex-col gap-3 border-ng/30 bg-ng-soft p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="training-status-card" data-status="failed">
        <div><div className="font-semibold text-ng">El entrenamiento falló</div><div className="text-xs text-slate-600" data-testid="training-error-text">{model.error}</div></div>
        <Button variant="danger" size="sm" onClick={onRetry} data-testid="training-retry-button">Reintentar</Button>
      </Card>
    );
  }
  const ng = model.ng_detected_pct === null ? "sin fotos NG para medir" : `detecta ${fmtPct(model.ng_detected_pct)} de las fotos NG`;
  return (
    <Card className="border-ok/30 bg-ok-soft p-4" data-testid="training-status-card" data-status="ready">
      <div className="font-semibold text-ok">Modelo listo · umbral {fmtNum(model.threshold)} · {ng} · {fmtPct(model.ok_false_ng_pct)} de falsos NG</div>
      {model.calibration === "holdout" && (
        <div className="mt-1 text-xs text-slate-600" data-testid="training-calibration-note">
          Umbral calibrado con {model.holdout_ok_count} fotos OK que el modelo no vio al entrenar (entrenó con {model.train_ok_count}).
        </div>
      )}
    </Card>
  );
}

function ModelsList({ models, onChanged }: { models: AiModel[]; onChanged: () => void }) {
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [confirm, setConfirm] = useState<{ kind: "activate" | "delete"; model: AiModel } | null>(null);
  const saveThreshold = async (m: AiModel) => {
    const v = Number((edits[m.id] ?? "").replace(",", "."));
    if (!(v > 0)) {
      toast.error("El umbral tiene que ser un número mayor que 0.");
      return;
    }
    try {
      await api.put(`/models/${m.id}/threshold`, { threshold: v });
      toast.success("Umbral guardado");
      setEdits((e) => ({ ...e, [m.id]: undefined }));
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  if (!models.length) return null;
  return (
    <Card className="overflow-hidden" data-testid="models-list">
      <h3 className="border-b border-slate-100 px-5 py-3 font-display text-base font-semibold">Modelos de esta pieza</h3>
      <div className="divide-y divide-slate-100">
        {models.map((m) => (
          <div key={m.id} className="grid gap-3 px-5 py-3 text-sm md:grid-cols-[1.2fr,0.8fr,1.6fr,0.7fr,0.7fr,0.8fr,auto] md:items-center" data-testid="model-row">
            <div><div className="font-mono text-xs text-slate-400">{m.id.slice(0, 8)}</div>{fmtDateTime(m.created_at)}</div>
            <div className="font-mono">{m.image_count} + {m.image_count_feedback}</div>
            <div className="flex items-center gap-2">
              {m.status === "ready" ? (
                <>
                  <Input className="h-8 w-28 font-mono" value={edits[m.id] ?? String(m.threshold ?? "")} onChange={(e) => setEdits({ ...edits, [m.id]: e.target.value })} inputMode="decimal" data-testid="model-threshold-input" />
                  <Button size="sm" variant="secondary" disabled={edits[m.id] === undefined} onClick={() => saveThreshold(m)} data-testid="model-threshold-save-button">Guardar</Button>
                </>
              ) : <span className="text-slate-400">—</span>}
            </div>
            <div title="NG detectadas">{fmtPct(m.ng_detected_pct)}</div>
            <div title="Falsos NG">{fmtPct(m.ok_false_ng_pct)}</div>
            <div className="flex gap-1">
              {m.is_active && <Badge tone="ok" testId="model-active-badge">Activo</Badge>}
              <Badge tone={m.status === "failed" ? "ng" : "muted"}>{m.status === "ready" ? "Listo" : m.status === "training" ? "Entrenando" : "Falló"}</Badge>
            </div>
            <div className="flex gap-2">
              {!m.is_active && m.status === "ready" && <Button size="sm" variant="secondary" onClick={() => setConfirm({ kind: "activate", model: m })} data-testid="model-activate-button">Activar</Button>}
              {!m.is_active && m.status !== "training" && <Button size="sm" variant="ghost" onClick={() => setConfirm({ kind: "delete", model: m })} data-testid="model-delete-button">Borrar</Button>}
            </div>
          </div>
        ))}
      </div>
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        danger={confirm?.kind === "delete"}
        title={confirm?.kind === "delete" ? "Borrar modelo" : "Activar modelo"}
        message={confirm?.kind === "delete" ? "Se borra el modelo y sus archivos." : "Las próximas inspecciones de esta pieza usan este modelo."}
        confirmLabel={confirm?.kind === "delete" ? "Borrar" : "Activar"}
        onConfirm={async () => {
          if (confirm.kind === "delete") await api.del(`/models/${confirm.model.id}`);
          else await api.post(`/models/${confirm.model.id}/activate`);
          onChanged();
        }}
      />
    </Card>
  );
}

export function TrainingTab({ part, reloadPart }: { part: Part; reloadPart: () => void }) {
  const [models, setModels] = useState<AiModel[]>([]);
  const [version, setVersion] = useState(0);
  const [confirmTrain, setConfirmTrain] = useState(false);

  const loadModels = useCallback(() => api.get<AiModel[]>(`/parts/${part.id}/models`).then(setModels).catch(() => undefined), [part.id]);
  useEffect(() => {
    loadModels();
  }, [loadModels]);

  const latest = models[0];
  const evalModel = models.find((m) => m.is_active && m.status === "ready") || models.find((m) => m.status === "ready");
  const trainingId = latest?.status === "training" ? latest.id : null;
  useEffect(() => {
    if (!trainingId) return undefined;
    const t = setInterval(async () => {
      try {
        const m = await api.get<AiModel>(`/models/${trainingId}`);
        if (m.status !== "training") {
          loadModels();
          reloadPart();
        }
      } catch {
        // keep polling
      }
    }, 5000);
    return () => clearInterval(t);
  }, [trainingId, loadModels, reloadPart]);

  const refreshAll = () => {
    setVersion((v) => v + 1);
    reloadPart();
  };
  const ok = part.image_counts.ok;
  const train = async () => {
    await api.post<TrainResponse>(`/parts/${part.id}/train`, { include_feedback: false });
    await loadModels();
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-5 lg:grid-cols-[1.4fr,1fr]">
        <UploadBlock part={part} onDone={refreshAll} />
        <div className="space-y-4">
          <Counter part={part} />
          <div title={ok < MIN_OK ? `Faltan ${MIN_OK - ok} fotos OK` : undefined}>
            <Button className="w-full" size="lg" disabled={ok < MIN_OK || !!trainingId || !!part.is_demo} onClick={() => setConfirmTrain(true)} data-testid="train-model-button">Entrenar modelo</Button>
          </div>
          {part.is_demo ? <p className="text-center text-xs text-slate-500" data-testid="train-demo-hint">Las piezas demo no se entrenan. Para probar la IA, crear una pieza real con sus fotos.</p> : ok < MIN_OK && <p className="text-center text-xs text-slate-500" data-testid="train-missing-hint">Faltan {MIN_OK - ok} fotos OK</p>}
          {latest && <StatusCard model={latest} onRetry={() => setConfirmTrain(true)} />}
        </div>
      </div>
      <ModelsList models={models} onChanged={() => { loadModels(); reloadPart(); }} />
      {part.active_model && !part.is_demo && <TryPhotoCard part={part} />}
      {evalModel && <ModelEvaluationCard model={evalModel} />}
      <Gallery part={part} version={version} onChanged={refreshAll} />
      <ConfirmDialog open={confirmTrain} onClose={() => setConfirmTrain(false)} title="Entrenar modelo" message={`Se va a entrenar con ${ok} fotos OK. Tarda entre 5 y 10 minutos.`} confirmLabel="Entrenar" onConfirm={train} />
    </div>
  );
}
